import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { designType } from "services/constants/designTokens";
import { FilterSelect, StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import { CHECK_DATE, DEFAULT_MONTH, PAYROLL_MONTHS, SITES, buildIssues, monthRows, paymentPeriods, samplePayments } from "./lwfReportsData";
import { LwfOverviewTab } from "./LwfOverviewTab";
import { LwfRegisterTab } from "./LwfRegisterTab";
import { LwfPaymentsTab } from "./LwfPaymentsTab";
import { LwfValidationTab } from "./LwfValidationTab";

// Payroll > Reports > Statutory Reports > Labour Welfare Fund (screens
// LWF3–LWF6a). Built from finalised payroll: Cauver works out LWF for each
// site; the company pays the state Labour Welfare Board and records the
// payment here. Sample data until the LWF APIs exist; payments recorded
// here stay in memory for this visit.

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "register", label: "LWF Register" },
  { value: "payments", label: "Payments" },
  { value: "validation", label: "Validation" },
];
const ALL = "all";
const now = () => moment().format("YYYY-MM-DDTHH:mm:ss");

export const LwfStatutoryReports = ({ onBack, initialTab }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh sample data per company.
  return <LwfReportsScreen key={`${domain}:${topbarCompanyId}`} onBack={onBack} initialTab={initialTab} />;
};

const LwfReportsScreen = ({ onBack, initialTab }) => {
  const [tab, setTab] = useState(() => (TABS.some((item) => item.value === initialTab) ? initialTab : "overview"));
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [siteFilter, setSiteFilter] = useState(ALL);
  const [department, setDepartment] = useState(ALL);
  const [payments, setPayments] = useState(samplePayments);
  const [lastCheck, setLastCheck] = useState({ at: CHECK_DATE, trigger: "payroll finalised (auto)" });

  const monthInfo = PAYROLL_MONTHS.find((item) => item.value === month);
  const allRows = useMemo(() => monthRows(month), [month]);
  const departments = useMemo(() => [...new Set(allRows.map((row) => row.department))].sort(), [allRows]);
  const rows = allRows.filter((row) => (siteFilter === ALL || row.site_name === siteFilter) && (department === ALL || row.department === department));
  const sites = SITES.filter((site) => siteFilter === ALL || site.name === siteFilter);

  const allPeriods = useMemo(() => paymentPeriods(payments), [payments]);
  const periods = allPeriods.filter((period) => siteFilter === ALL || period.site === siteFilter);
  const allIssues = useMemo(() => buildIssues(month, allPeriods, lastCheck.at), [month, allPeriods, lastCheck]);
  const issues = allIssues.filter((item) => siteFilter === ALL || item.site === siteFilter);

  const record = (payment) => {
    setPayments((list) => [...list, { ...payment, id: Math.max(0, ...list.map((item) => item.id)) + 1, recorded_by: "You", recorded_at: now() }]);
    setLastCheck({ at: now(), trigger: "payment recorded (auto)" });
  };
  const reverse = (payment) => {
    setPayments((list) => [
      ...list,
      {
        id: Math.max(0, ...list.map((item) => item.id)) + 1,
        period: payment.period,
        type: "REVERSAL",
        reverses_id: payment.id,
        paid_on: moment().format("YYYY-MM-DD"),
        amount: -payment.amount,
        reference: `${payment.reference}-REV`,
        receipt: "",
        remarks: `Reverses ${payment.reference}`,
        recorded_by: "You",
        recorded_at: now(),
      },
    ]);
    setLastCheck({ at: now(), trigger: "payment reversed (auto)" });
  };

  const aside = (
    <Mui.Box sx={{ textAlign: { md: "right" } }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center" justifyContent={{ md: "flex-end" }}>
        <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
        <RequestStatusChip status="Finalized" colorCode="#12B76A" />
      </Mui.Stack>
      <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>
        Finalized {moment(monthInfo.finalizedAt).format("DD MMM YYYY, HH:mm")} · Version {monthInfo.version}
      </Mui.Typography>
    </Mui.Box>
  );

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Reports", "Statutory Reports", "Labour Welfare Fund"]}
      title="LWF Reports"
      subtitle="Built from finalised payroll. Cauver works out LWF for each site — your organisation pays it to the state Labour Welfare Board."
      aside={aside}
      onBack={onBack}
    >
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <FilterSelect label="Payroll Month" value={month} onChange={setMonth} options={PAYROLL_MONTHS.map((item) => ({ value: item.value, label: item.label }))} />
        <FilterSelect
          label="Site"
          value={siteFilter}
          onChange={setSiteFilter}
          options={[{ value: ALL, label: "All sites" }, ...SITES.map((site) => ({ value: site.name, label: site.name }))]}
        />
        <FilterSelect
          label="Department"
          value={department}
          onChange={setDepartment}
          options={[{ value: ALL, label: "All departments" }, ...departments.map((item) => ({ value: item, label: item }))]}
        />
      </Mui.Stack>

      <StatutoryTabs value={tab} onChange={setTab} tabs={TABS} />

      {tab === "overview" ? <LwfOverviewTab monthInfo={monthInfo} rows={rows} sites={sites} periods={periods} issues={issues} goTo={setTab} /> : null}
      {tab === "register" ? <LwfRegisterTab key={month} monthInfo={monthInfo} rows={rows} /> : null}
      {tab === "payments" ? <LwfPaymentsTab monthInfo={monthInfo} periods={periods} payments={payments} onRecord={record} onReverse={reverse} /> : null}
      {tab === "validation" ? (
        <LwfValidationTab issues={issues} lastCheck={lastCheck} onRecheck={() => setLastCheck({ at: now(), trigger: "Re-check now" })} goTo={setTab} />
      ) : null}
    </StatutoryPage>
  );
};
